import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Tooltip, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { adminApi } from '../../services/api';

const Clusters = () => {
    const [clusters, setClusters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCluster, setEditingCluster] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const fetchClusters = async () => {
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                setClusters(res.data.data.list || []);
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    const handleAdd = () => {
        setEditingCluster(null);
        form.resetFields();
        form.setFieldsValue({
            db_port: 3306,
            is_active: true,
            is_default: false,
            accept_new: true,
            region: 'default',
            weight: 100,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingCluster(record);
        form.setFieldsValue({ ...record, public_api_url: record.metadata?.public_api_url || '', max_domains: record.metadata?.max_domains || 0 });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await adminApi.delete(`/webmail/clusters/${id}`);
            if (res.data.code === 0) {
                message.success('删除成功');
                fetchClusters();
            } else {
                message.error(res.data.message || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const submitData = { ...values };
            // 编辑时，如果密码为空则不提交（表示不修改）
            if (editingCluster) {
                if (!submitData.db_password) {
                    delete submitData.db_password;
                }
                if (!submitData.api_key) {
                    delete submitData.api_key;
                }
                const res = await adminApi.put(`/webmail/clusters/${editingCluster.id}`, submitData);
                if (res.data.code === 0) {
                    message.success('更新成功');
                    setModalVisible(false);
                    fetchClusters();
                } else {
                    message.error(res.data.message || '更新失败');
                }
            } else {
                const res = await adminApi.post('/webmail/clusters', submitData);
                if (res.data.code === 0) {
                    message.success('创建成功');
                    setModalVisible(false);
                    fetchClusters();
                } else {
                    message.error(res.data.message || '创建失败');
                }
            }
        } catch (error) {
            message.error('操作失败');
        } finally {
            setSubmitting(false);
        }
    };

    const handleTest = async (id) => {
        try {
            const res = await adminApi.post(`/webmail/clusters/${id}/test`);
            if (res.data.code === 0) {
                if (res.data.data.online) {
                    message.success('连接正常');
                } else {
                    message.warning('连接失败');
                }
                fetchClusters();
            }
        } catch (error) {
            message.error('测试失败');
        }
    };

    const handleSetDefault = async (id) => {
        try {
            const res = await adminApi.post(`/webmail/clusters/${id}/default`);
            if (res.data.code === 0) {
                message.success('设置成功');
                fetchClusters();
            } else {
                message.error(res.data.message || '设置失败');
            }
        } catch (error) {
            message.error('设置失败');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: '名称',
            dataIndex: 'name',
            render: (text, record) => (
                <Space>
                    {text}
                    {record.is_default && <Tag color="gold"><StarFilled /> 默认</Tag>}
                </Space>
            ),
        },
        {
            title: '数据库连接',
            render: (_, record) => (
                <span>{record.db_user}@{record.db_host}:{record.db_port}/{record.db_name}</span>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (status) => (
                <Tag color={status === 'online' ? 'green' : status === 'maintenance' ? 'orange' : 'red'}>
                    {status === 'online' ? <CheckCircleOutlined /> : <CloseCircleOutlined />} {status}
                </Tag>
            ),
        },
        {
            title: '启用',
            dataIndex: 'is_active',
            render: (active) => active ? <Tag color="blue">启用</Tag> : <Tag>禁用</Tag>,
        },
        {
            title: '新域名',
            dataIndex: 'accept_new',
            render: (v) => v === false || v === 0 ? <Tag>不接新域名</Tag> : <Tag color="green">接新域名</Tag>,
        },
        {
            title: '最后检查',
            dataIndex: 'last_check_at',
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
        {
            title: '操作',
            width: 280,
            render: (_, record) => (
                <Space>
                    <Tooltip title="测试连接">
                        <Button size="small" icon={<SyncOutlined />} onClick={() => handleTest(record.id)} />
                    </Tooltip>
                    {!record.is_default && (
                        <Tooltip title="设为默认">
                            <Button size="small" icon={<StarOutlined />} onClick={() => handleSetDefault(record.id)} />
                        </Tooltip>
                    )}
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                    <Popconfirm
                        title="确定删除此集群吗？"
                        description="删除后该集群下的域名与用户配置将无法访问，此操作不可恢复"
                        okText="删除"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record.id)}
                        disabled={record.is_default}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={record.is_default}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="邮件集群管理"
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    添加集群
                </Button>
            }
        >
            <Table
                columns={columns}
                dataSource={clusters}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description="暂无集群，点击右上角“添加集群”创建" /> }}
            />

            <Modal
                title={editingCluster ? '编辑集群' : '添加集群'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="name"
                        label="集群名称"
                        rules={[{ required: true, message: '请输入集群名称' }]}
                    >
                        <Input placeholder="如：主集群" />
                    </Form.Item>
                    <Form.Item
                        name="db_host"
                        label="数据库主机"
                        rules={[{ required: true, message: '请输入数据库主机' }]}
                    >
                        <Input placeholder="如：104.194.8.4" />
                    </Form.Item>
                    <Form.Item name="db_port" label="端口">
                        <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="db_user"
                        label="用户名"
                        rules={[{ required: true, message: '请输入数据库用户名' }]}
                    >
                        <Input placeholder="数据库用户名" />
                    </Form.Item>
                    <Form.Item
                        name="db_password"
                        label="数据库密码"
                        rules={[{ required: !editingCluster, message: '请输入数据库密码' }]}
                        extra={editingCluster ? '留空则不修改' : null}
                    >
                        <Input.Password placeholder="数据库密码" autoComplete="new-password" visibilityToggle />
                    </Form.Item>
                    <Form.Item
                        name="db_name"
                        label="数据库名"
                        rules={[{ required: true, message: '请输入数据库名' }]}
                    >
                        <Input placeholder="如：webmail" />
                    </Form.Item>
                    <Form.Item name="api_url" label="API地址">
                        <Input placeholder="如：http://mail.example.com:8089" />
                    </Form.Item>
                    <Form.Item name="public_api_url" label="用户端 API" tooltip="webmail 页面登录后直连的地址">
                        <Input placeholder="https://webmail-api.yeap.cloud" />
                    </Form.Item>
                    <Space>
                        <Form.Item name="region" label="地区">
                            <Input placeholder="default" style={{ width: 140 }} />
                        </Form.Item>
                        <Form.Item name="weight" label="权重">
                            <InputNumber min={0} max={1000} />
                        </Form.Item>
                        <Form.Item name="max_domains" label="域名上限" tooltip="0 = 不限">
                            <InputNumber min={0} />
                        </Form.Item>
                    </Space>
                    <Form.Item
                        name="api_key"
                        label="API密钥"
                        extra={editingCluster ? '留空则不修改' : null}
                    >
                        <Input.Password placeholder="API密钥" autoComplete="new-password" visibilityToggle />
                    </Form.Item>
                    <Space>
                        <Form.Item name="is_active" valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                        </Form.Item>
                        <Form.Item name="is_default" valuePropName="checked">
                            <Switch checkedChildren="默认" unCheckedChildren="非默认" />
                        </Form.Item>
                        <Form.Item name="accept_new" valuePropName="checked" tooltip="关闭后新开通不再分到这个集群,已有域名不受影响">
                            <Switch checkedChildren="接新域名" unCheckedChildren="不接新域名" />
                        </Form.Item>
                    </Space>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Clusters;
